// Self-driving evaluator — the Tier-S spine. When the app is open, runs the
// local (Ollama-first) evaluator on the "Aprendizado entre sessões" cadence and
// PROPOSES the gated rules via a reviewable PR (openLearnedRulesPR → isolated
// worktree off origin/main → gh pr create). The loop GOVERNS, it does NOT commit
// straight to the branch: nothing lands in .apolorules until a human merges the PR
// (main is branch-protected). Proposals are already gated twice server-side
// (external-signal gate + isActionableLearnedRule quality gate) + the F3 content
// gate in the writer, so the PR content is safe to review; the writer dedupes by rule_id.

import { runEvaluatorViaLocal, resolveEvaluatorEngine } from './evaluatorLocal.js';
import {
  openLearnedRulesPR,
  listLearnedRules,
  fetchEvaluatorPending,
  dismissEvaluatorPending,
} from '../hooks/useEvaluator.js';

/** Daily — mirrors the EVALUATOR_DEFAULT_CRON ('0 4 * * *') cadence. */
export const AUTO_RUN_MIN_INTERVAL_MS = 24 * 60 * 60 * 1000;

/**
 * Is the evaluator due to auto-run? Pure.
 * @param {{ lastRunAt?: number, now: number, minIntervalMs?: number }} opts
 */
export function shouldAutoRunEvaluator({ lastRunAt, now, minIntervalMs = AUTO_RUN_MIN_INTERVAL_MS }) {
  if (!Number.isFinite(now)) return false;
  if (!lastRunAt) return true;
  return now - lastRunAt >= minIntervalMs;
}

/**
 * Run the local evaluator and PROPOSE its gated rules via a PR (no direct commit
 * to the branch). Returns { ok, applied, considered }. `applied` = count of rules
 * in the opened PR (0 if no change / no PR).
 *
 * @param {string} root session worktree
 * @param {string} sessionId
 * @param {{ runFn?: Function, applyFn?: Function, listFn?: Function, engineFn?: Function }} [deps]
 */
export async function runAndApplyEvaluator(root, sessionId, {
  runFn = runEvaluatorViaLocal,
  applyFn = openLearnedRulesPR, // loop PROPOSES via PR — never commits to the branch directly
  listFn = listLearnedRules,
  engineFn = resolveEvaluatorEngine,
} = {}) {
  if (!root) return { ok: false, reason: 'no_root' };
  if (!engineFn()) return { ok: false, reason: 'no_local_engine' };

  const res = await runFn('all');
  const proposals = Array.isArray(res?.proposals) ? res.proposals : [];
  if (proposals.length === 0) {
    return { ok: true, applied: 0, considered: res?.considered ?? 0, error: res?.error || res?.model_error };
  }

  const existing = await listFn(root);
  const lastRuleId = proposals[proposals.length - 1].rule_id;
  const applyRes = await applyFn(root, [...existing, ...proposals], lastRuleId, { sessionId });
  if (applyRes?.ok === false) return { ok: false, reason: 'apply_failed', error: applyRes.error };
  return { ok: true, applied: applyRes?.changed === false ? 0 : proposals.length, considered: res?.considered ?? 0 };
}

/** A stashed proposal is usable only if it carries non-empty rule TEXT (the
 * server already gated content + external-signal before stashing; this is just a
 * defensive floor so a corrupt stash can never inject an empty/garbage rule). */
function usableStashedProposal(p) {
  return Boolean(p && typeof p === 'object' && typeof p.rule === 'string' && p.rule.trim());
}

/**
 * Propose the rules the SERVER-SIDE cloud evaluator stashed while the app was
 * closed (E3-T01). On app open we load the stash and PROPOSE the gated rules via
 * the SAME PR path (openLearnedRulesPR) the local auto-run uses — never a direct
 * commit. The proposals were already double-gated server-side (external-signal +
 * isActionableLearnedRule) BEFORE stashing, so opening the PR is safe; the writer
 * dedupes by rule_id and we clear the stash on success so the next open does not re-propose.
 *
 * Unlike runAndApplyEvaluator this needs NO local engine — the rules were
 * already produced in the cloud. Best-effort + defensive: empty stash → no-op;
 * apply failure → ok:false (stash kept so a future open can retry); any throw is
 * swallowed into ok:false and NEVER disturbs the session.
 *
 * @param {string} root session worktree
 * @param {string} sessionId
 * @param {{ fetchFn?, listFn?, applyFn?, dismissFn? }} [deps]
 */
export async function applyStashedEvaluatorProposals(root, sessionId, {
  fetchFn = fetchEvaluatorPending,
  listFn = listLearnedRules,
  applyFn = openLearnedRulesPR, // loop PROPOSES via PR — never commits to the branch directly
  dismissFn = dismissEvaluatorPending,
} = {}) {
  if (!root) return { ok: false, reason: 'no_root' };
  try {
    const { pending } = await fetchFn();
    const proposals = (Array.isArray(pending?.proposals) ? pending.proposals : []).filter(usableStashedProposal);
    if (proposals.length === 0) return { ok: true, applied: 0, considered: pending?.considered ?? 0 };

    const existing = await listFn(root);
    const lastRuleId = proposals[proposals.length - 1].rule_id;
    const applyRes = await applyFn(root, [...existing, ...proposals], lastRuleId, { sessionId });
    if (applyRes?.ok === false) return { ok: false, reason: 'apply_failed', error: applyRes.error };

    // Applied (or already deduped) — clear the stash so the toast/next open is clean.
    try { await dismissFn(); } catch { /* best-effort */ }
    return {
      ok: true,
      applied: applyRes?.changed === false ? 0 : proposals.length,
      considered: pending?.considered ?? 0,
    };
  } catch (err) {
    return { ok: false, reason: 'threw', error: err?.message || String(err) };
  }
}
